import type { MenuItem, PlaceMenu } from '@shared/types';

/**
 * Dining menus for the Babcock National tab.
 *
 * The Enclave items are transcribed from the club's published PDF
 * (Dining → Menu on babcocknationalhoa.com), dated 9.21.26.
 * Prices are the printed strings. Wine and cocktails stay in that PDF
 * (linked as the full menu) because the drink page is a two-column image.
 *
 * Watershed Grill is transcribed from the printed menu in the 12 Oct 2026
 * owner photo. Prices are the printed strings. The club site still has no
 * itemised Watershed menu, so that sheet links to the HOA dining page.
 *
 * Poolside service and the concession window have no itemised menu. Their
 * sheets use MenuSheet's not_published copy and link to the HOA dining page.
 */

export const ENCLAVE_MENU_PDF_URL =
  'https://www.babcocknationalhoa.com/_filelib/FileCabinet/The_Enclave_ALL_MENUS_9.21.26.pdf';

export const NATIONAL_DINING_PAGE_URL = 'https://www.babcocknationalhoa.com/dining';

const ENCLAVE_READ_AT = '2026-09-21T15:00:00.000Z';

type Row = [name: string, price: string | null, description?: string];

function items(section: string, rows: Row[], id: { n: number }): MenuItem[] {
  return rows.map(([name, price, description]) => ({
    id: id.n++,
    section,
    name,
    price,
    description: description ?? null,
  }));
}

function block(section: string, rows: Row[], id: { n: number }) {
  return { section, items: items(section, rows, id) };
}

const ADD_PROTEIN: Row[] = [
  ['Chicken', '6', 'Grilled or blackened'],
  ['Shrimp', '8', 'Grilled or blackened'],
  ['4oz Filet', '15', 'Grilled or blackened'],
  ['Salmon', '10', 'Grilled or blackened'],
  ['Tofu', '7', 'Grilled or blackened'],
];

function enclaveMenu(): PlaceMenu {
  const id = { n: 1 };
  const sections = [
    block(
      'Lunch · Appetizers',
      [
        [
          'Wings',
          '14/15',
          'Choose bone-in or boneless, served with celery & carrots and your choice of ranch or bleu cheese. Sauces: Buffalo (Mild | Medium | Hot), Garlic Parmesan, Lemon Pepper Dry Rub, Sweet Thai Chili, Bang Bang',
        ],
        ['Brussels Sprouts', '15', 'candied pecans, smoked bacon, dried cranberries, honey mustard'],
        ['Meatballs', '14', 'house-made with plum tomato sauce and garlic toast'],
        ['Tuna & Cucumber Crudo', '18', 'yellowfin tuna, cucumber, seaweed salad, jalapeño & ponzu sauce'],
        [
          'Hummus Board (v)',
          '15',
          'roasted garlic & lemon hummus, marinated olives, cucumber, baby carrots, celery, sweet drop peppers, served with warm pita',
        ],
        ['Shrimp Cocktail', '15', 'citrus poached jumbo shrimp, classic cocktail sauce, lemon garnish'],
        [
          'Fried Dill Pickle Dip & Pretzels (v)',
          '13',
          'creamy dill pickle dip, crispy breadcrumbs & warm pretzel bites',
        ],
      ],
      id,
    ),
    block('Lunch · Soups', [['Soup Du Jour', 'Cup: 8 | Bowl: 10']], id),
    block(
      'Lunch · Salads',
      [
        [
          'Wedge Salad',
          '14',
          'iceberg lettuce, cucumber, red onion, smoked bacon, baby tomato, bleu cheese & green goddess dressing',
        ],
        [
          'Babcock Ranch Salad (v)',
          '13',
          'mixed greens, goat cheese, roasted gold & red beets, Marcona almonds, mandarin oranges, red onions, carrots & citrus vinaigrette',
        ],
        [
          'Caesar Salad',
          '12',
          'romaine lettuce, parmesan cheese, garlic & herb croutons, hard-boiled eggs, & caesar dressing',
        ],
        [
          'Pork Belly & Watermelon Salad',
          '13',
          'crispy pork belly, watermelon, baby arugula, pickled red onion, feta cheese, & brown sugar vinaigrette',
        ],
      ],
      id,
    ),
    block('Lunch · Add Protein', ADD_PROTEIN, id),
    block(
      'Lunch · Entrees',
      [
        [
          'Enclave Patty Melt',
          '16',
          '8 oz patty, toasted sourdough bread, caramelized onion, Swiss cheese & special sauce',
        ],
        [
          'Babcock Burger',
          '18',
          '8 oz patty, brioche bun, shaved iceberg lettuce, tomato, red onion, pickle & choice of cheese',
        ],
        [
          'Chicken Club Sandwich',
          '16',
          'grilled chicken breast, brioche bun, Applewood-smoked bacon, shaved iceberg lettuce, tomato, red onion & garlic mayo',
        ],
        ['Meatball Sliders', '14', 'house-made meatballs, plum tomato sauce, Hawaiian roll'],
        [
          'Florida Grouper Sandwich',
          'MKT',
          'blackened or grilled, brioche bun, shaved iceberg lettuce, plum tomato, red onion, & key lime tartar sauce',
        ],
        [
          'California Turkey Club',
          '15',
          'roasted turkey, crisp romaine lettuce, vine ripe tomato, bacon, avocado, & mayo on multigrain bread',
        ],
        [
          'California BLT',
          '13',
          'sourdough, Applewood-smoked bacon, vine ripe tomato, crisp lettuce, avocado & garlic aioli',
        ],
        [
          'Mediterranean Portabella Burger (v)',
          '14',
          'brioche bun, feta cheese, cucumber, tomato, red onion, arugula & tzatziki sauce',
        ],
      ],
      id,
    ),
    block(
      'Lunch · Sides',
      [
        ['Fries', '5'],
        ['Sweet Potato Fries', '6'],
        ['Onion Rings', '6'],
        ['Fruit', '4'],
        ['Slaw', '4'],
        ['Tator Tots', '6'],
      ],
      id,
    ),
    block(
      'Dinner · Appetizers',
      [
        ['Brussel Sprouts', '15', 'candied pecans, smoked bacon, dried cranberries, honey mustard'],
        ['Tuna & Cucumber Crudo', '18', 'yellowfin tuna, cucumber, seaweed salad, jalapeño & ponzu sauce'],
        [
          'Filet Tips',
          '18',
          'au poivre, sautéed filet tips, brandy peppercorn cream sauce, served with warm pita',
        ],
        [
          'Fried Dill Pickle Dip & Pretzels (v)',
          '13',
          'creamy dill pickle dip, crispy breadcrumbs, & warm pretzel bites',
        ],
        [
          'Hummus Board (v)',
          '15',
          'roasted garlic & lemon hummus, marinated olives, cucumber, baby carrots, celery, sweet drop peppers, served with warm pita',
        ],
        ['Boom Boom Shrimp', '17', 'crispy fried jumbo shrimp, spicy Sriracha mayo, scallion'],
        ['Shrimp Cocktail', '15', 'citrus poached jumbo shrimp, classic cocktail sauce, & lemon garnish'],
        ['Meatballs', '14', 'house-made plum tomato sauce, garlic toast'],
      ],
      id,
    ),
    block(
      'Dinner · Salads',
      [
        [
          'Wedge Salad',
          '14',
          'iceberg lettuce, cucumber, red onion, smoked bacon, baby tomato, bleu cheese, green goddess dressing',
        ],
        [
          'Pork Belly & Watermelon Salad',
          '13',
          'crispy pork belly, watermelon, baby arugula, feta cheese, pickled red onion, brown sugar vinaigrette',
        ],
        [
          'Southern Caesar',
          '12',
          'romaine lettuce, parmesan cheese, garlic & herb hushpuppies, hard boiled eggs, spicy Caesar dressing',
        ],
        [
          'Babcock Ranch Salad (v)',
          '13',
          'mixed greens, goat cheese, roasted gold & red beets, marcona almonds, mandarin oranges, red onion, carrots, citrus vinaigrette',
        ],
      ],
      id,
    ),
    block('Dinner · Add Protein', ADD_PROTEIN, id),
    block(
      'Dinner · Soups',
      [
        ['French Onion', '9'],
        ['Soup Du Jour', 'Cup: 8 | Bowl: 10'],
      ],
      id,
    ),
    block(
      'Dinner · Entrees',
      [
        [
          'Filet Mignon | 8 oz',
          '40',
          'hand cut, yukon gold mash potato, house vegetable, brandy peppercorn demi-glace',
        ],
        [
          'Pork Tenderloin',
          '28',
          'smoked paprika-rubbed pork tenderloin, yukon gold mashed potatoes, house vegetable, roasted garlic & hot honey',
        ],
        [
          'Seafood Paella',
          '26',
          'local shrimp, mussels, clams, local fish, chorizo sausage, fresh peas, tomato & saffron rice',
        ],
        ['Grilled New York Strip Steak | 12 oz', '37', 'hand-cut pomme frites, cowboy butter'],
        [
          'Red Wine Braised Short Rib',
          '34',
          'yukon gold mash potato, house vegetable, demi-glace',
        ],
        [
          'Meatball Pappardelle',
          '24',
          'house-made meatballs, fresh pappardelle pasta, plum tomato sauce & garlic toast',
        ],
        ['Grilled Salmon', '27', 'sautéed spinach risotto, grilled lemon'],
        [
          'Chicken & Yellow Rice',
          '22',
          'smoked chicken thighs, chorizo sausage, plum tomatoes, green peas, roasted garlic & saffron rice',
        ],
        [
          'Spicy Tofu Rice Bowl (v)',
          '20',
          'gochujang tofu, steamed jasmine rice, kimchi, julienned carrots, spinach, sesame seeds',
        ],
        [
          'Gulf Shrimp & Ravioli',
          '25',
          'jumbo gulf shrimp, garlic, lemon, white wine, fresh parsley & butter, served over spinach & mascarpone ravioli with garlic bread',
        ],
        [
          'Shawarma-Marinated Cauliflower Steak (v)',
          '21',
          'Jasmine rice, house vegetable, tahini dressing',
        ],
        [
          'Babcock Burger',
          '18',
          '8 oz patty, brioche bun, cheddar cheese, shaved iceberg lettuce, tomato, red onion, pickles, secret sauce with house cut fries',
        ],
        ['Catch of the Week', 'MKT', "Chef's creation of the week"],
      ],
      id,
    ),
  ];
  const itemCount = sections.reduce((n, sec) => n + sec.items.length, 0);
  return {
    placeSlug: 'the-enclave-at-babcock-national',
    placeName: 'The Enclave',
    sections,
    itemCount,
    status: 'ok',
    note: null,
    sourceUrl: ENCLAVE_MENU_PDF_URL,
    menuUrl: ENCLAVE_MENU_PDF_URL,
    fetchedAt: ENCLAVE_READ_AT,
  };
}

const WATERSHED_READ_AT = '2026-10-12T15:00:00.000Z';

function watershedMenu(): PlaceMenu {
  const id = { n: 1 };
  const sections = [
    block(
      'Snacks & Wings',
      [
        ['Mozzarella Sticks', '12', 'House-Made Marinara, Parmesan Cheese'],
        ['Bavarian Pretzel Sticks', '8', 'Warm Soft Pretzel with Cheese Sauce'],
        ['Bang Bang Shrimp', '15', 'Signature Spicy Sauce'],
        [
          'SR31 BBQ Nachos',
          '14',
          'Crispy Tortilla Chips, Cheddar Cheese Sauce, Sour Cream, Guacamole, Pico de Gallo, Pickled Jalapeño, Melted Shredded Cheddar',
        ],
        ['Add Protein: Chicken', '5'],
        ['Add Protein: Shrimp', '9'],
        ['Add Protein: Tofu', '7'],
        ['Add Protein: Pulled Pork', '7'],
        ['Add Protein: Steak Tips', '10'],
        [
          'Chicken Wings (Bone In / Boneless)',
          '14/15',
          'Served with Carrots & Celery, & Ranch or Bleu Cheese. Sauces: Buffalo (Mild | Medium | Hot), Garlic Parmesan, Lemon Pepper Dry Rub, BBQ, Sweet Thai Chili, Teriyaki, Bang Bang',
        ],
        ['Fries', '5'],
        ['Sweet Potato Fries', '6'],
        ['Chips', '4'],
        ['Onion Rings', '6'],
        ['Coleslaw', '4'],
        ['Fruit', '4'],
      ],
      id,
    ),
    block(
      'Pizza',
      [
        [
          'Build Your Own',
          '15',
          'Shredded Mozzarella Cheese, Pick Your Own Toppings, up to Four. Toppings: Pepperoni, Sausage, Bacon, Ham, Mushrooms, Onions, Tomato',
        ],
        ['Margherita', '14', 'Fresh & Shredded Mozzarella, Vine-Ripened Tomato, Fresh Basil, Olive Oil'],
        ["Meat Lover's", '15', 'Pepperoni, Sausage, Bacon, Ham, Shredded Mozzarella'],
        ['The Truffle Shuffle', '15', 'Truffle Oil, Roasted Mushrooms, Shredded Mozzarella, Caramelized Onion'],
        [
          'The Sandpit',
          '14',
          'White Pizza, Garlic Ricotta Cheese, Shredded Mozzarella, Parmesan Cheese, Olive Oil, & Fresh Herbs',
        ],
        ['Optional: Cauliflower Crust (GF)', '3'],
      ],
      id,
    ),
    block(
      'Salads',
      [
        [
          'Babcock Ranch Salad (GF)',
          '12',
          'Mixed Greens, Roasted Gold & Red Beets, Marcona Almonds, Mandarin Oranges, Red Onion, Carrots, & Citrus Vinaigrette',
        ],
        [
          'Caesar Salad',
          '10',
          'Crisp Romaine Lettuce, Shaved Parmesan Cheese, House-Made Croutons, & Caesar Dressing',
        ],
        ['Add Protein: Chicken', '5'],
        ['Add Protein: Shrimp', '9'],
        ['Add Protein: Filet Tips', '10'],
        ['Add Protein: Tofu', '7'],
        ['Add Protein: Salmon', '9'],
      ],
      id,
    ),
    block(
      'Handhelds & Burgers',
      [
        [
          'Philly Cheesesteak',
          '18',
          'Shaved Ribeye Steak, Toasted Amoroso Roll, Cheddar Cheese Sauce, Sauteed Peppers, Onions',
        ],
        ['Add Toppings: Mushrooms', '2'],
        [
          'Watershed Burger',
          '18',
          '8 oz Burger Patty, Brioche Bun, Cheddar Cheese, Shaved Iceberg Lettuce, Vine-Ripened Tomato, Red Onion',
        ],
        [
          'Triple Tail Sandwich',
          'MKT',
          'Blackened or Grilled, Shaved Iceberg Lettuce, Vine-Ripened Tomato, Red Onion, Tartar Sauce, Brioche Bun',
        ],
        [
          'Back Nine Burger',
          '18',
          '8 oz Burger Patty, Brioche Bun, Cheddar Cheese, Shaved Iceberg Lettuce, Vine-Ripened Tomato, Red Onion, Bacon, Caramelized Onion, & BBQ Ranch',
        ],
        ['Greenway Grilled Cheese', '13', 'Sourdough Bread, Melted Gruyere Cheese, Applewood Bacon, Caramelized Onions'],
        [
          'California Turkey Club',
          '15',
          'Roasted Turkey, Crisp Romaine Lettuce, Vine-Ripened Tomato, Bacon, Avocado, Mayo, Multigrain Bread',
        ],
        [
          'Poolside Caesar Wrap',
          '14',
          'Your Choice of Lemon Herb Chicken Breast or Lemon Herb Crispy Tofu, Warm Tortilla, Crisp Romaine Lettuce, Parmesan Cheese, & Caesar Dressing',
        ],
        [
          'Babcock Brioche Chicken Sandwich',
          '15',
          'Lemon & Herb Marinated Grilled Chicken, Brioche Bun, Provolone Cheese, Avocado, Balsamic Tomatoes',
        ],
        [
          'Served with Choice of Side',
          null,
          'Fries, Onion Rings, Sweet Potato Fries, Chips, Coleslaw, Fruit',
        ],
      ],
      id,
    ),
    block(
      'Bowls',
      [
        ['Thai Salmon Bowl', '18', 'Salmon Filet, Jasmine Rice, Vegetable Medley, Sesame Peanut Sauce'],
        [
          'Miso Sweet Potato Bowl (vegan)',
          '17',
          'Jasmine Rice, Vegetable Medley, Avocado, Sweet Potato, Miso Glaze',
        ],
      ],
      id,
    ),
  ];
  const itemCount = sections.reduce((n, sec) => n + sec.items.length, 0);
  return {
    placeSlug: 'the-watershed-grill-babcock-national',
    placeName: 'Watershed Grill',
    sections,
    itemCount,
    status: 'ok',
    note: null,
    sourceUrl: null,
    menuUrl: NATIONAL_DINING_PAGE_URL,
    fetchedAt: WATERSHED_READ_AT,
  };
}

function unpublishedMenu(slug: string, name: string): PlaceMenu {
  return {
    placeSlug: slug,
    placeName: name,
    sections: [],
    itemCount: 0,
    status: 'not_published',
    note: null,
    sourceUrl: NATIONAL_DINING_PAGE_URL,
    menuUrl: NATIONAL_DINING_PAGE_URL,
    fetchedAt: null,
  };
}

export interface NationalDiningMenu {
  /** Outlet name exactly as printed in the Dining card. */
  name: string;
  slug: string;
  menu: PlaceMenu;
  /** Printed menu date, shown instead of a "read N days ago" age. */
  datedLabel?: string;
  /** Primary link label when the href is not a menu. */
  linkLabel?: string;
}

const ENCLAVE_DATED_LABEL = 'Menu dated Sep 21, 2026';
const DINING_PAGE_LINK_LABEL = 'HOA dining page';

export const NATIONAL_DINING_MENUS: NationalDiningMenu[] = [
  {
    name: 'The Enclave',
    slug: 'the-enclave-at-babcock-national',
    menu: enclaveMenu(),
    datedLabel: ENCLAVE_DATED_LABEL,
  },
  {
    name: 'Watershed Grill',
    slug: 'the-watershed-grill-babcock-national',
    menu: watershedMenu(),
    linkLabel: DINING_PAGE_LINK_LABEL,
  },
  {
    name: 'Poolside service',
    slug: 'babcock-national-poolside-service',
    menu: unpublishedMenu('babcock-national-poolside-service', 'Poolside service'),
    linkLabel: DINING_PAGE_LINK_LABEL,
  },
  {
    name: 'Concession window',
    slug: 'babcock-national-concession-window',
    menu: unpublishedMenu('babcock-national-concession-window', 'Concession window'),
    linkLabel: DINING_PAGE_LINK_LABEL,
  },
];

export function nationalDiningMenu(name: string): NationalDiningMenu | undefined {
  return NATIONAL_DINING_MENUS.find((row) => row.name === name);
}
